/**
 * LivePreview V2 — bogaty podgląd strony hodowli zgodnie z mockupem.
 *
 * Layout:
 *  - Header: logo + nav + CTA
 *  - Hero: zdjęcie + headline + subtitle + 2 CTA
 *  - O hodowli: opis + 4 cechy z fioletowymi ikonkami
 *  - Nasi reproduktorzy: karty psów
 *  - Stopka: gdzie profil będzie widoczny
 *
 * Modes: desktop / mobile (toggle w prawym górnym rogu)
 */
import { useState } from 'react';
import {
  Monitor, Smartphone, Heart, Shield, Users, MessageCircle as MsgIcon,
  Phone, MessageSquare, Award, Globe, Sparkles, Search, Check,
} from 'lucide-react';
import { getImageUrl } from '@/utils/imageUrl';

const FEATURES = [
  { icon: Heart, title: 'Domowa hodowla', desc: 'Psy wychowywane w domu z miłością.' },
  { icon: Shield, title: 'Zdrowie przede wszystkim', desc: 'Badania, profilaktyka i najlepsza opieka.' },
  { icon: Users, title: 'Najlepsze linie FCI', desc: 'Sprawdzone pochodzenie i piękne charaktery.' },
  { icon: MsgIcon, title: 'Stały kontakt', desc: 'Wspieramy właścicieli na każdym etapie.' },
];

const VISIBLE_IN = ['Google', 'Bing', 'Mapy', 'Katalog APISAI', 'Facebook', 'Asystenci'];

function HeroImg({ src, alt }) {
  if (src) {
    return <img src={getImageUrl(src) || src} alt={alt} className="absolute inset-0 w-full h-full object-cover" />;
  }
  return (
    <div className="absolute inset-0 bg-gradient-to-br from-orange-100 via-orange-50 to-orange-50 flex items-center justify-center">
      <Heart className="w-12 h-12 text-orange-300" />
    </div>
  );
}

function LandingMock({ data, isMobile }) {
  const name = data.name?.trim() || 'Twoja hodowla';
  const city = data.city?.trim();
  const breeds = data.breeds || [];
  // Linia związku / przynależności (np. „FCI · ZKwP (FCI)") — znak zaufania.
  const orgLabel = data.organization?.trim();
  const orgLine = [data.is_fci ? 'FCI' : null, orgLabel && orgLabel !== 'Brak / nie podano' ? orgLabel : null]
    .filter(Boolean)
    .filter((v, i, a) => a.indexOf(v) === i)
    .join(' · ');
  const description = data.description?.trim();
  const dogs = data.dogs || [];
  const gallery = data.gallery || [];

  const containerCls = isMobile
    ? 'w-[300px] mx-auto border-[10px] border-gray-800 rounded-[32px] overflow-hidden shadow-2xl bg-white'
    : 'w-full rounded-xl overflow-hidden shadow-xl bg-white border border-gray-200';

  const titleSize = isMobile ? 'text-xl' : 'text-2xl';
  const heroPad = isMobile ? 'p-4' : 'p-8';

  return (
    <div className={containerCls} data-testid="live-preview-landing">
      {/* Top nav */}
      <div className={`flex items-center justify-between bg-white border-b border-gray-100 ${isMobile ? 'px-3 py-2' : 'px-5 py-3'}`}>
        <div className="flex items-center gap-2 min-w-0">
          <div className="w-6 h-6 rounded bg-gradient-to-br from-orange-500 to-orange-600 flex-shrink-0" />
          <span className={`font-bold text-gray-900 truncate ${isMobile ? 'text-xs' : 'text-sm'}`}>{name}</span>
        </div>
        {!isMobile && (
          <div className="flex items-center gap-3 text-[11px] text-gray-600">
            <span>O nas</span>
            <span>Nasze psy</span>
            <span>Szczenięta</span>
            <span>Galeria</span>
            <span className="bg-orange-500 text-white px-2.5 py-1 rounded-md font-medium">Napisz do nas</span>
          </div>
        )}
      </div>

      {/* Hero */}
      <div className={`relative ${isMobile ? 'h-44' : 'h-56'} w-full bg-gray-100`}>
        <HeroImg src={data.cover_image} alt={name} />
        <div className="absolute inset-0 bg-gradient-to-r from-black/65 via-black/30 to-transparent" />
        <div className={`relative h-full flex flex-col justify-center ${heroPad} text-white`}>
          <h2 className={`font-bold leading-tight ${titleSize} max-w-xs`}>
            Hodowla z pasją i miłością do psów
          </h2>
          {breeds.length > 0 && (
            <p className={`mt-1 opacity-95 ${isMobile ? 'text-xs' : 'text-sm'} max-w-xs`}>
              {breeds.slice(0, 2).join(' · ')} — zdrowie, charakter i najlepsze linie.
            </p>
          )}
          {orgLine && (
            <p className={`mt-1 font-semibold tracking-wide opacity-95 ${isMobile ? 'text-[10px]' : 'text-xs'}`}>
              {orgLine}
            </p>
          )}
          <div className="flex gap-2 mt-3">
            <span className={`bg-orange-500 text-white rounded-md font-medium ${isMobile ? 'text-[10px] px-2 py-1' : 'text-xs px-3 py-1.5'}`}>
              Zobacz szczenięta
            </span>
            <span className={`bg-white/95 text-gray-900 rounded-md font-medium ${isMobile ? 'text-[10px] px-2 py-1' : 'text-xs px-3 py-1.5'}`}>
              Skontaktuj się
            </span>
          </div>
        </div>
      </div>

      {/* O hodowli */}
      <div className={isMobile ? 'p-3' : 'p-5'}>
        <h3 className={`font-bold text-gray-900 ${isMobile ? 'text-sm' : 'text-base'} mb-2`}>O naszej hodowli</h3>
        {description ? (
          <p className={`text-gray-700 leading-relaxed ${isMobile ? 'text-[11px]' : 'text-xs'} line-clamp-4`}>
            {description}
          </p>
        ) : (
          <p className={`text-gray-400 italic ${isMobile ? 'text-[11px]' : 'text-xs'}`}>
            Tu pojawi się Twój opis hodowli…
          </p>
        )}

        {/* 4 features */}
        <div className={`grid grid-cols-2 gap-2 mt-3 ${isMobile ? '' : 'gap-3'}`}>
          {FEATURES.map(({ icon: Icon, title, desc }) => (
            <div key={title} className="flex gap-2 items-start">
              <div className="w-7 h-7 rounded-full bg-orange-100 flex items-center justify-center flex-shrink-0">
                <Icon className="w-3.5 h-3.5 text-orange-500" />
              </div>
              <div className="min-w-0">
                <p className={`font-semibold text-gray-900 leading-tight ${isMobile ? 'text-[10px]' : 'text-[11px]'}`}>
                  {title}
                </p>
                {!isMobile && (
                  <p className="text-[10px] text-gray-500 line-clamp-1">{desc}</p>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Nasi reproduktorzy */}
      {dogs.length > 0 && (
        <div className={`bg-gray-50 ${isMobile ? 'p-3' : 'p-5'} border-t border-gray-100`}>
          <h3 className={`font-bold text-gray-900 ${isMobile ? 'text-sm' : 'text-base'} mb-3`}>
            Nasi reproduktorzy
          </h3>
          <div className={`grid gap-2 ${isMobile ? 'grid-cols-1' : 'grid-cols-2'}`}>
            {dogs.slice(0, 2).map((dog, i) => (
              <div key={i} className="bg-white rounded-lg shadow-sm overflow-hidden border border-gray-200">
                <div className={`${isMobile ? 'h-24' : 'h-28'} bg-gray-100`}>
                  {dog.photo ? (
                    <img src={getImageUrl(dog.photo) || dog.photo} alt={dog.name} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full bg-gradient-to-br from-orange-50 to-orange-50 flex items-center justify-center">
                      <Heart className="w-6 h-6 text-orange-300" />
                    </div>
                  )}
                </div>
                <div className="p-2">
                  {dog.role && (
                    <span className="inline-block px-2 py-0.5 bg-orange-100 text-orange-600 text-[10px] font-medium rounded-full">
                      {dog.role}
                    </span>
                  )}
                  <p className={`font-bold text-gray-900 truncate mt-1 ${isMobile ? 'text-[11px]' : 'text-xs'}`}>
                    {dog.name || 'Twój pies'}
                  </p>
                  {dog.achievements?.[0] && (
                    <p className="flex items-center gap-1 text-[10px] text-gray-600 mt-0.5">
                      <Award className="w-2.5 h-2.5 text-amber-500" /> {dog.achievements[0]}
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Galeria thumbs */}
      {gallery.length > 0 && (
        <div className={`${isMobile ? 'px-3 py-2' : 'px-5 py-3'}`}>
          <p className={`font-semibold text-gray-700 ${isMobile ? 'text-[11px]' : 'text-xs'} mb-1.5`}>Galeria</p>
          <div className="grid grid-cols-4 gap-1">
            {gallery.slice(0, 4).map((img, i) => (
              <div key={i} className="aspect-square rounded bg-gray-100 overflow-hidden">
                <img src={getImageUrl(img) || img} alt="" className="w-full h-full object-cover" />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Contact CTA */}
      <div className={`bg-orange-50 border-t border-orange-100 ${isMobile ? 'p-3' : 'p-4'}`}>
        <p className={`font-bold text-gray-900 ${isMobile ? 'text-xs' : 'text-sm'}`}>
          Masz pytania? Napisz do nas.
        </p>
        <div className="flex flex-wrap gap-1.5 mt-2">
          {data.whatsapp && (
            <span className="flex items-center gap-1 px-2 py-1 bg-white rounded text-[10px] text-gray-700 border border-gray-200">
              <MessageSquare className="w-2.5 h-2.5 text-emerald-600" /> WhatsApp
            </span>
          )}
          {data.phone && (
            <span className="flex items-center gap-1 px-2 py-1 bg-white rounded text-[10px] text-gray-700 border border-gray-200">
              <Phone className="w-2.5 h-2.5 text-orange-500" /> {data.phone}
            </span>
          )}
          {!data.whatsapp && !data.phone && (
            <span className="text-[10px] text-gray-400 italic">Dodaj kontakt w kroku 6…</span>
          )}
        </div>
      </div>

      {/* Visible in */}
      <div className={`bg-gray-50 border-t border-gray-100 ${isMobile ? 'p-2.5' : 'p-3'}`}>
        <p className={`text-gray-500 ${isMobile ? 'text-[9px]' : 'text-[10px]'} text-center mb-1`}>
          Po opublikowaniu profil będzie widoczny w:
        </p>
        <p className={`text-orange-600 ${isMobile ? 'text-[9px]' : 'text-[10px]'} text-center font-medium`}>
          {VISIBLE_IN.join(' · ')}
        </p>
        {city && (
          <p className={`text-gray-400 ${isMobile ? 'text-[9px]' : 'text-[10px]'} text-center mt-1`}>{city}</p>
        )}
      </div>
    </div>
  );
}

export default function LivePreviewV2({ data }) {
  const [mode, setMode] = useState('desktop');

  return (
    <div className="w-full" data-testid="kennel-wizard-v2-live-preview">
      {/* Header z toggle + ecosystem context */}
      <div className="mb-3 px-1">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <p className="text-xs uppercase tracking-wider font-semibold text-gray-500">
              Twoja strona · podgląd na żywo
            </p>
            <span className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 text-[10px] font-semibold ring-1 ring-emerald-200/60" data-testid="live-preview-live-badge">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
              LIVE
            </span>
          </div>
          <div className="flex gap-0.5 bg-gray-100 rounded-lg p-0.5">
            <button
              type="button"
              onClick={() => setMode('desktop')}
              data-testid="live-preview-mode-desktop"
              className={`w-8 h-8 rounded-md flex items-center justify-center transition-all ${
                mode === 'desktop' ? 'bg-white shadow-sm text-orange-600' : 'text-gray-500'
              }`}
              aria-label="Desktop"
            >
              <Monitor className="w-4 h-4" />
            </button>
            <button
              type="button"
              onClick={() => setMode('mobile')}
              data-testid="live-preview-mode-mobile"
              className={`w-8 h-8 rounded-md flex items-center justify-center transition-all ${
                mode === 'mobile' ? 'bg-white shadow-sm text-orange-600' : 'text-gray-500'
              }`}
              aria-label="Mobile"
            >
              <Smartphone className="w-4 h-4" />
            </button>
          </div>
        </div>
        {/* Ecosystem signals strip — buduje emotional reward loop */}
        <div className="mt-2 flex items-center gap-1.5 flex-wrap" data-testid="live-preview-ecosystem-signals">
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-orange-50 text-orange-600 text-[10px] font-medium ring-1 ring-orange-200/50">
            <Globe className="w-3 h-3" /> Widoczne publicznie
          </span>
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 text-[10px] font-medium ring-1 ring-blue-200/50">
            <Search className="w-3 h-3" /> Gotowe pod Google
          </span>
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-orange-50 text-orange-700 text-[10px] font-medium ring-1 ring-orange-200/50">
            <Sparkles className="w-3 h-3" /> Indeksowane w wyszukiwarkach
          </span>
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 text-[10px] font-medium ring-1 ring-emerald-200/50">
            <Check className="w-3 h-3" /> Mobile-ready
          </span>
        </div>
      </div>

      {/* Canvas */}
      <div className="bg-gray-50 rounded-2xl p-4 min-h-[640px] flex items-start justify-center border border-gray-100">
        <LandingMock data={data} isMobile={mode === 'mobile'} />
      </div>
    </div>
  );
}
